import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { getBundleValueUp } from 'lib/bundleAndSave/getBundleValueUp'
import { isBundleOffer } from 'lib/offer/offerTypes'
import { safeDivideAndCeil } from 'lib/maths/mathUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'

interface HotelOfferPricing {
  propertyFees: number;
  totalPrice: number;
  totalMemberPrice: number;
  totalMemberPriceWithFlights: number;
  totalValue: number;
  totalValueWithFlights: number;
  memberPriceOrPrice: number;
  /**
   * How much of a percentage the value compared to the price is
   * This takes into account the users membership status and offer membership pricing
   */
  valueOffPercent: number;
  totalPriceWithFlights: number;
  flightPrice: number;
  saleUnit: string;
}

interface OfferPricing {
  total: number;
  value: number | undefined;
  duration?: number;
  saleUnit: string;
  propertyFees?: number;
  valueOffPercent?: number;
  displayPricingAsPerPerson?: boolean;
  memberTotal?: number;
}

export interface PriceOptions {
  flightPrice?: number;
  numberOfNights?: number;
  showMemberPrice?: boolean;
}

function pricePerUnit(basePriceDetails: HotelOfferPricing, divisor: number, flightPrice: number = 0): Omit<HotelOfferPricing, 'saleUnit'> {
  const totalValue = safeDivideAndCeil(basePriceDetails.totalValue, divisor)
  return {
    propertyFees: basePriceDetails.propertyFees,
    totalPrice: safeDivideAndCeil(basePriceDetails.totalPrice, divisor),
    totalPriceWithFlights: safeDivideAndCeil(basePriceDetails.totalPrice + flightPrice, divisor),
    flightPrice: safeDivideAndCeil(flightPrice, divisor),
    totalMemberPrice: safeDivideAndCeil(basePriceDetails.totalMemberPrice, divisor),
    totalMemberPriceWithFlights: safeDivideAndCeil(basePriceDetails.totalMemberPrice + flightPrice, divisor),
    totalValue,
    totalValueWithFlights: totalValue + flightPrice,
    memberPriceOrPrice: safeDivideAndCeil(basePriceDetails.memberPriceOrPrice, divisor),
    valueOffPercent: basePriceDetails.valueOffPercent,
  }
}

function priceDefault(basePriceDetails: HotelOfferPricing, flightPrice?: number): HotelOfferPricing {
  return {
    ...pricePerUnit(basePriceDetails, 1, flightPrice),
    saleUnit: basePriceDetails.saleUnit,
  }
}

function packagePriceDefault(
  pkg: App.Package | App.BundlePackageWithPrice,
  offer: App.Offer | App.OfferSummary | App.BundleOffer | App.BundleOfferSummary,
  isActiveLuxPlusMember: boolean,
): HotelOfferPricing {
  const propertyFees = pkg.propertyFees

  const totalPrice = pkg.price + propertyFees
  let totalValue = (isActiveLuxPlusMember ? pkg.memberValue : pkg.value) + propertyFees
  const totalMemberPrice = pkg.memberPrice > 0 ? pkg.memberPrice + propertyFees : 0
  const memberPriceOrPrice = totalMemberPrice > 0 && isActiveLuxPlusMember ? totalMemberPrice : totalPrice
  let valueOffPercent = getValueOffPercent(totalValue, memberPriceOrPrice)
  const saleUnit = offer.saleUnit

  if (isBundleOffer(offer) && 'packages' in pkg) {
    const bundleValue = getBundleValueUp(offer, pkg, isActiveLuxPlusMember)
    valueOffPercent = bundleValue.hotelDiscountPercent ?? 0
    totalValue = bundleValue.valuedUpTo
  }

  return {
    propertyFees,
    totalPrice,
    totalMemberPrice,
    totalValue,
    memberPriceOrPrice,
    valueOffPercent,
    saleUnit,
    totalValueWithFlights: totalValue,
    flightPrice: 0,
    totalPriceWithFlights: 0,
    totalMemberPriceWithFlights: 0,
  }
}

function ratePriceDefault(offer: App.BedbankOffer | App.BedbankOfferSummary | undefined, rate?: App.BedbankRate): HotelOfferPricing | undefined {
  const propertyFees = rate?.totals?.propertyFees ?? 0
  const hotelPrice = rate ? rate.totals.inclusive : offer?.sell?.price
  if (!hotelPrice) return
  const totalValue = rate?.value ?? offer?.sell?.value ?? hotelPrice
  const totalPrice = hotelPrice + propertyFees

  return {
    propertyFees,
    totalPrice,
    totalValue,
    valueOffPercent: getValueOffPercent(totalValue, totalPrice),
    flightPrice: 0,
    memberPriceOrPrice: totalPrice,
    totalMemberPrice: 0,
    totalPriceWithFlights: 0,
    totalMemberPriceWithFlights: 0,
    totalValueWithFlights: totalValue,
    saleUnit: 'total',
  }
}

/**
 * Returns pricing for offer or package if supplied without taking into account occupancies/rooms
 */
export function useOfferPackagePrice(
  pkg: App.Package | App.BundlePackageWithPrice,
  offer: App.Offer | App.OfferSummary | App.BundleOffer | App.BundleOfferSummary,
  options?: PriceOptions,
) {
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const basePriceDetails = packagePriceDefault(pkg, offer, options?.showMemberPrice ?? canViewLuxPlusBenefits)
  return priceDefault(basePriceDetails, options?.flightPrice)
}

/**
 * Returns pricing for bedbank offer or rate if supplied without taking into account occupancies/rooms
 */
export function useOfferRatePrice(offer: App.BedbankOffer | App.BedbankOfferSummary | undefined, rate?: App.BedbankRate, options?: PriceOptions) {
  const basePriceDetails = ratePriceDefault(offer, rate)
  if (!basePriceDetails) return
  return priceDefault(basePriceDetails, options?.flightPrice)
}

function getBaseOfferPrice(pricing: OfferPricing) {
  return {
    ...pricing,
    valueOffPercent: getValueOffPercent(pricing.value || 0, pricing.total),
    total: pricing.total + (pricing.propertyFees || 0),
  }
}

/**
 * Hook for handling different pricing formats
 */
export function useOfferPrice(pricing: OfferPricing): OfferPricing {
  return getBaseOfferPrice(pricing)
}

/**
 * Hook for handling different pricing formats
 */
export function useOfferPriceWithMember(pricing: OfferPricing): OfferPricing {
  const basePricing = getBaseOfferPrice(pricing)
  return {
    ...basePricing,
    memberTotal: pricing.memberTotal,
  }
}
